/**
 * Money for the pricing screens. The API sends integer PENCE; people read
 * pounds. Never show the word "credits": a balance is £12.40, a text is 8p.
 *
 * Older settings/wallet responses only carry `walletCredits` (1 credit = 2p).
 * Prefer the pence fields when they are there, and fall back to credits × 2p.
 */

export const LEGACY_PENCE_PER_CREDIT = 2;

const toInt = (v) => {
    if (v == null || v === '' || typeof v === 'boolean') return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : null;
};

/**
 * £12.40, -£2.40, £0.00.
 *   whole: true drops .00 on whole pounds (£25, £5) — for prices, not balances.
 *   plus: true prefixes a + on positive amounts (an add-on: "+£40").
 */
export function formatPence(pence, opts = {}) {
    const p = toInt(pence);
    if (p == null) return opts.fallback != null ? opts.fallback : 'None';
    const neg = p < 0;
    const abs = Math.abs(p);
    const pounds = Math.floor(abs / 100);
    const pennies = abs % 100;
    const poundsText = pounds.toLocaleString('en-GB');
    const body = opts.whole && pennies === 0
        ? `£${poundsText}`
        : `£${poundsText}.${String(pennies).padStart(2, '0')}`;
    if (neg) return `-${body}`;
    return opts.plus && p > 0 ? `+${body}` : body;
}

/** A price: £25, £59, £5, £10.50 — whole pounds without the pennies. */
export const formatPrice = (pence, opts = {}) => formatPence(pence, { whole: true, ...opts });

/**
 * A per-message price: under £1 it is pence ("8p", "2p", "1.5p"), over it
 * reads as pounds. Used for the cost of a text or an email.
 */
export function formatUnitPence(pence) {
    const n = pence == null || pence === '' ? NaN : Number(pence);
    if (!Number.isFinite(n)) return 'None';
    if (Math.abs(n) < 100) {
        const rounded = Math.round(n * 10) / 10;
        return `${rounded}p`;
    }
    return formatPence(n);
}

/** Integer pence from a pounds amount (10 → 1000). */
export const poundsToPence = (gbp) => Math.round((Number(gbp) || 0) * 100);

/** Pence for a legacy credits figure. */
export const creditsToPence = (credits, pencePerCredit = LEGACY_PENCE_PER_CREDIT) =>
    Math.round((Number(credits) || 0) * (Number(pencePerCredit) || LEGACY_PENCE_PER_CREDIT));

/** Credits for a pence figure, for endpoints that still store credits. */
export const penceToCredits = (pence, pencePerCredit = LEGACY_PENCE_PER_CREDIT) =>
    Math.round((Number(pence) || 0) / (Number(pencePerCredit) || LEGACY_PENCE_PER_CREDIT));

const ppcOf = (r) => (r && r.creditCosts && Number(r.creditCosts.pencePerCredit)) || LEGACY_PENCE_PER_CREDIT;

/**
 * The message balance in pence from a settings/wallet response:
 * balancePence (or walletPence) when present, else walletCredits × 2p.
 * null when the response says nothing about a wallet.
 */
export function walletBalancePence(r) {
    if (!r) return null;
    if (r.balancePence != null) return toInt(r.balancePence);
    if (r.walletPence != null) return toInt(r.walletPence);
    if (r.walletCredits != null) return creditsToPence(r.walletCredits, ppcOf(r));
    return null;
}

/**
 * What one text and one email cost, in pence, from whatever the response has:
 * the new pricing's `prices` block, pence fields on the wallet response, or
 * the legacy credit costs × 2p. Never hard-coded beyond that last fallback.
 */
export function messagePrices(r, usagePrices) {
    const p = usagePrices || (r && r.prices) || {};
    const legacy = (r && r.creditCosts) || {};
    const ppc = ppcOf(r);
    const text = p.textPence != null ? Number(p.textPence)
        : r && r.textPence != null ? Number(r.textPence)
            : legacy.sms != null ? Number(legacy.sms) * ppc : 3 * ppc;
    const email = p.emailPence != null ? Number(p.emailPence)
        : r && r.emailPence != null ? Number(r.emailPence)
            : legacy.email != null ? Number(legacy.email) * ppc : 1 * ppc;
    return { textPence: text, emailPence: email };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "1 Nov" (or "1 Nov 2027" outside the current year). '' when there is no date. */
export function shortDate(value, now = new Date()) {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
    return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

/** "Resets 1 Nov", or '' with no date. */
export const resetsLine = (value, now) => {
    const d = shortDate(value, now);
    return d ? `Resets ${d}` : '';
};

export default { formatPence, formatPrice, formatUnitPence, walletBalancePence, messagePrices, shortDate, resetsLine };
